import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Play } from "lucide-react";

const Field = ({ label, name, value, onChange, type = "text", placeholder, testid, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">{label}</span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base focus:outline-none focus:border-black transition-colors"
    />
  </label>
);

const SocialVideoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const videoRef = useRef(null);
  const posterRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [form, setForm] = useState({
    title: "",
    handle: "@manzanohomes",
    views: "",
    poster_url: "",
    video_url: "",
    position: 0,
    featured: true,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/social-videos");
        const found = data.find((v) => v.id === id);
        if (!found) {
          toast.error("Video not found");
          navigate("/admin");
          return;
        }
        setForm((f) => ({ ...f, ...found }));
      } catch {
        toast.error("Failed to load video");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : (name === "position" ? Number(value) || 0 : value),
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000, // 10 minutes for big files
      });
      setForm((f) => ({ ...f, video_url: data.url }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const handlePosterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPoster(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, poster_url: data.url }));
      toast.success("Poster uploaded");
    } catch {
      toast.error("Poster upload failed");
    } finally {
      setUploadingPoster(false);
      if (posterRef.current) posterRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.video_url) {
      toast.error("Video file or URL is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/social-videos/${id}`, form);
        toast.success("Video updated.");
      } else {
        await api.post("/admin/social-videos", form);
        toast.success("Video added.");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="admin-social-video-form" className="min-h-screen bg-white pb-32">
      <header className="border-b border-black/10 px-6 md:px-12 py-6 sticky top-0 bg-white z-10">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] hover:opacity-60"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 pt-16">
        <p className="overline mb-4">{isEdit ? "Edit social video" : "New social video"}</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-16">
          {isEdit ? (
            <>Update <em className="italic font-light">video tile</em></>
          ) : (
            <>Add a <em className="italic font-light">social video</em></>
          )}
        </h1>

        <form onSubmit={handleSave} className="space-y-16">
          <section>
            <p className="overline mb-8">Video file</p>
            <div className="flex flex-col sm:flex-row items-start gap-8">
              {form.video_url ? (
                <div className="relative w-40 aspect-[9/16] bg-black overflow-hidden">
                  <video
                    src={`${resolveImage(form.video_url)}#t=0.5`}
                    poster={form.poster_url ? resolveImage(form.poster_url) : undefined}
                    preload="metadata"
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play size={24} className="text-white" fill="white" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, video_url: "" }))}
                    className="absolute -top-2 -right-2 bg-white border border-black/15 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                    data-testid="remove-video"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-40 aspect-[9/16] bg-gray-100 flex items-center justify-center text-[10px] uppercase tracking-widest text-black/30">
                  No video
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => videoRef.current?.click()}
                  disabled={uploadingVideo}
                  data-testid="upload-video-button"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploadingVideo ? "Uploading..." : form.video_url ? "Replace video" : "Upload .mp4"}
                </button>
                <p className="text-xs text-black/40 mt-3 max-w-md">
                  Portrait (9:16) videos work best — typical Instagram/TikTok format. MP4 / MOV / WebM accepted.
                </p>
                <div className="mt-6">
                  <Field
                    label="Or paste a direct video URL"
                    name="video_url"
                    value={form.video_url}
                    onChange={handleChange}
                    placeholder="https://.../video.mp4"
                    testid="v-video-url"
                  />
                </div>
              </div>
            </div>
            <input ref={videoRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
          </section>

          <section>
            <p className="overline mb-8">Poster image (optional)</p>
            <div className="flex items-start gap-8">
              {form.poster_url ? (
                <div className="relative w-32 aspect-[9/16] bg-gray-100">
                  <img
                    src={resolveImage(form.poster_url)}
                    alt="Poster"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, poster_url: "" }))}
                    className="absolute -top-2 -right-2 bg-white border border-black/15 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:text-red-600"
                    data-testid="remove-poster"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="w-32 aspect-[9/16] bg-gray-100 flex items-center justify-center text-[10px] uppercase tracking-widest text-black/30 text-center">
                  No poster
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => posterRef.current?.click()}
                  disabled={uploadingPoster}
                  data-testid="upload-poster-button"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-black/15 hover:border-black text-[11px] uppercase tracking-[0.24em] transition-colors disabled:opacity-50"
                >
                  <Upload size={14} /> {uploadingPoster ? "Uploading..." : form.poster_url ? "Replace poster" : "Upload poster"}
                </button>
                <p className="text-xs text-black/40 mt-3 max-w-md">
                  A poster frame is shown before the video loads. If omitted, the first video frame is used.
                </p>
              </div>
            </div>
            <input ref={posterRef} type="file" accept="image/*" onChange={handlePosterUpload} className="hidden" />
          </section>

          <section>
            <p className="overline mb-8">Caption</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. Malibu Cliffside Tour"
                testid="v-title"
                full
              />
              <Field
                label="Handle"
                name="handle"
                value={form.handle}
                onChange={handleChange}
                placeholder="@manzanohomes"
                testid="v-handle"
              />
              <Field
                label="Views"
                name="views"
                value={form.views}
                onChange={handleChange}
                placeholder="e.g. 1.2M"
                testid="v-views"
              />
            </div>
          </section>

          <section>
            <p className="overline mb-8">Visibility</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <Field
                label="Sort Position (lower = first)"
                name="position"
                type="number"
                value={form.position}
                onChange={handleChange}
                testid="v-position"
              />
              <label className="flex items-center gap-4 cursor-pointer pt-8">
                <input
                  type="checkbox"
                  name="featured"
                  checked={form.featured}
                  onChange={handleChange}
                  data-testid="v-featured"
                  className="w-5 h-5 accent-black"
                />
                <span className="text-sm">Show on homepage</span>
              </label>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-black/10">
            <button
              type="submit"
              disabled={saving}
              data-testid="v-save"
              className="inline-flex items-center justify-center px-12 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Save changes" : "Publish video"}
            </button>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center px-10 py-4 bg-transparent text-black border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-black hover:text-white transition-all duration-500"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
};

export default SocialVideoForm;
